import React, { useCallback, useState } from 'react';
import { Input } from '@vehix/portal/components/ui/input';
import { Badge } from '@vehix/portal/components/ui/badge';
import { X } from 'lucide-react';

interface EmailChipsInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  id?: string;
  placeholder?: string;
  maxRecipients?: number;
}

/**
 * Same permissive check the dialogs used before this component existed.
 * Deliberately not tightened — a stricter pattern would start rejecting
 * addresses that work today.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Separators people actually paste: commas, semicolons, and any whitespace. */
const SPLIT_RE = /[\s,;]+/;

const DEFAULT_MAX = 20;

/**
 * Multi-recipient email entry.
 *
 * Replaces a plain input whose only commit path was pressing Enter on a single
 * well-formed address — anything else (a pasted distribution list, a trailing
 * comma, a typo) did nothing at all: no chip, no error, no feedback. That
 * silence is why adding several recipients felt broken rather than merely
 * fiddly.
 */
// eslint-disable-next-line max-lines-per-function
export const EmailChipsInput: React.FC<EmailChipsInputProps> = ({
  value,
  onChange,
  id,
  placeholder = 'name@example.com',
  maxRecipients = DEFAULT_MAX
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Adds every valid address in `text`, returning whatever didn't parse so the
   * caller can leave it in the field for the user to fix rather than discard it.
   */
  const commit = useCallback(
    (text: string): string => {
      const tokens = text.split(SPLIT_RE).filter(Boolean);
      if (tokens.length === 0) return '';

      // Dedupe case-insensitively while keeping the casing first seen — the old
      // `includes` check was case-sensitive, so Ops@x.com and ops@x.com both landed.
      const seen = new Set(value.map(e => e.toLowerCase()));
      const added: string[] = [];
      const rejected: string[] = [];

      for (const token of tokens) {
        if (!EMAIL_RE.test(token)) {
          rejected.push(token);
          continue;
        }
        const key = token.toLowerCase();
        if (seen.has(key)) continue;
        if (value.length + added.length >= maxRecipients) {
          rejected.push(token);
          continue;
        }
        seen.add(key);
        added.push(token);
      }

      if (added.length > 0) onChange([...value, ...added]);

      if (rejected.length === 0) {
        setError(null);
      } else if (value.length + added.length >= maxRecipients) {
        setError(`Up to ${maxRecipients} recipients.`);
      } else {
        setError(
          rejected.length === 1
            ? `"${rejected[0]}" is not a valid email address.`
            : `${rejected.length} addresses were not valid.`
        );
      }

      return rejected.join(', ');
    },
    [maxRecipients, onChange, value]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      // Tab with an empty field should still move focus.
      if (e.key === 'Tab' && draft.trim() === '') return;
      e.preventDefault();
      setDraft(commit(draft));
      return;
    }
    // Backspace on an empty field removes the last chip — the usual token-input
    // affordance, and faster than aiming for a 12px close button.
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!SPLIT_RE.test(text.trim())) return; // single token — let it type normally
    e.preventDefault();
    setDraft(commit(`${draft} ${text}`));
  };

  const remove = (email: string) => {
    setError(null);
    onChange(value.filter(e => e !== email));
  };

  return (
    <div className='space-y-1.5'>
      <Input
        id={id}
        value={draft}
        placeholder={placeholder}
        aria-invalid={error !== null}
        onChange={e => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => setDraft(commit(draft))}
        className={error ? 'border-red-500' : ''}
      />

      {error ? (
        <p className='text-xs text-red-500'>{error}</p>
      ) : (
        <p className='text-xs text-muted-foreground'>
          Press Enter or paste a list. {value.length}/{maxRecipients} recipients.
        </p>
      )}

      {value.length > 0 && (
        <div className='flex flex-wrap gap-1.5' role='list'>
          {value.map(email => (
            <Badge key={email} variant='secondary' className='gap-1 pr-1' role='listitem'>
              {email}
              <button
                type='button'
                onClick={() => remove(email)}
                aria-label={`Remove ${email}`}
                className='rounded-full p-0.5 hover:bg-muted'
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
